import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import toast from 'react-hot-toast';
import { notificationAPI } from '../services/realApi';
import { useAuth } from './AuthContext';
import { IS_STATIC_DEMO } from '../services/config';
import { getNextDeliveryIn } from '../services/demoNotifications';
import type { Notification, NotificationFilters } from '../types';

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  total: number;
  loading: boolean;
  error: string | null;
  isPanelOpen: boolean;
  filter: 'all' | 'unread';
  fetchNotifications: (options?: Partial<NotificationFilters>) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<boolean>;
  markAllAsRead: () => Promise<boolean>;
  deleteNotification: (notificationId: string) => Promise<boolean>;
  togglePanel: () => void;
  openPanel: () => void;
  closePanel: () => void;
  changeFilter: (newFilter: 'all' | 'unread') => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export const useNotifications = (): NotificationContextValue => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const pollIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const TOASTED_STORAGE_KEY = 'notification_toasted_ids';
  const toastedIdsRef = useRef<Set<string>>(new Set(
    (() => { try { return JSON.parse(sessionStorage.getItem(TOASTED_STORAGE_KEY) || '[]'); } catch { return []; } })()
  ));

  const fetchNotifications = useCallback(async (options: Partial<NotificationFilters> = {}) => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const filters = {
        unreadOnly: options.unreadOnly || filter === 'unread',
        limit: options.limit || 50,
        offset: options.offset || 0,
        category: options.category
      };

      const result = await notificationAPI.getNotifications(filters);

      if (result.success) {
        setNotifications(result.data ?? []);
        setUnreadCount(result.unreadCount ?? 0);
        setTotal(result.total ?? 0);
      } else {
        setError(result.error ?? null);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, filter]);

  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const result = await notificationAPI.getUnreadCount();
      if (result.success) {
        setUnreadCount(result.unreadCount ?? 0);
      }
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, [isAuthenticated]);

  const markAsRead = useCallback(async (notificationId: string): Promise<boolean> => {
    try {
      const result = await notificationAPI.markAsRead(notificationId);
      if (result.success) {
        setNotifications(prev =>
          prev.map(n =>
            n.id === notificationId ? { ...n, is_read: true } : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      return false;
    }
  }, []);

  const markAllAsRead = useCallback(async (): Promise<boolean> => {
    try {
      const result = await notificationAPI.markAllAsRead();
      if (result.success) {
        setNotifications(prev =>
          prev.map(n => ({ ...n, is_read: true }))
        );
        setUnreadCount(0);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      return false;
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId: string): Promise<boolean> => {
    try {
      const result = await notificationAPI.deleteNotification(notificationId);
      if (result.success) {
        const deletedNotification = notifications.find(n => n.id === notificationId);
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        setTotal(prev => prev - 1);
        if (deletedNotification && !deletedNotification.is_read) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to delete notification:', err);
      return false;
    }
  }, [notifications]);

  const togglePanel = useCallback(() => {
    setIsPanelOpen(prev => !prev);
  }, []);

  const openPanel = useCallback(() => {
    setIsPanelOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  const changeFilter = useCallback((newFilter: 'all' | 'unread') => {
    setFilter(newFilter);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
    } else {
      setNotifications([]);
      setUnreadCount(0);
      setTotal(0);
    }
  }, [isAuthenticated, filter, fetchNotifications]);

  const showNotificationToast = useCallback((notification: Notification) => {
    const severityStyles: Record<string, { border: string; icon: string; badge: string }> = {
      critical: {
        border: 'border-l-4 border-red-500',
        icon: 'text-red-500',
        badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      },
      warning: {
        border: 'border-l-4 border-yellow-500',
        icon: 'text-yellow-500',
        badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      },
      info: {
        border: 'border-l-4 border-blue-500',
        icon: 'text-blue-500',
        badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      },
    };

    const style = severityStyles[notification.severity] || severityStyles.info;

    toast.custom(
      (t) => (
        <div
          className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-sm w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg pointer-events-auto ${style.border} cursor-pointer`}
          onClick={() => {
            toast.dismiss(t.id);
            notificationAPI.markAsRead(notification.id);
            globalThis.location.href = '/hub/notifications';
          }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') {
            toast.dismiss(t.id);
            notificationAPI.markAsRead(notification.id);
            globalThis.location.href = '/hub/notifications';
          }; }} role="button" tabIndex={0}
        >
          <div className="p-4">
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {notification.title}
                  </p>
                  <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${style.badge}`}>
                    {notification.severity}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                  {notification.message}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 capitalize">
                  {notification.category}
                </p>
              </div>
              <button
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                onClick={(e) => {
                  e.stopPropagation();
                  toast.dismiss(t.id);
                }}
              >
                &times;
              </button>
            </div>
          </div>
        </div>
      ),
      { duration: 5000, id: `notif-toast-${notification.id}` }
    );
  }, []);

  const checkForNewToasts = useCallback((currentNotifications: Notification[]) => {
    let changed = false;
    for (const n of currentNotifications) {
      if (!toastedIdsRef.current.has(n.id) && !n.is_read) {
        toastedIdsRef.current.add(n.id);
        showNotificationToast(n);
        changed = true;
      }
    }
    if (changed) {
      sessionStorage.setItem(TOASTED_STORAGE_KEY, JSON.stringify(Array.from(toastedIdsRef.current)));
    }
  }, [showNotificationToast, TOASTED_STORAGE_KEY]);

  useEffect(() => {
    if (!isAuthenticated) return;

    try {
      toastedIdsRef.current = new Set(JSON.parse(sessionStorage.getItem(TOASTED_STORAGE_KEY) || '[]'));
    } catch {
      toastedIdsRef.current = new Set();
    }

    fetchUnreadCount();
    fetchNotifications();

    if (IS_STATIC_DEMO) {
      const schedulePoll = () => {
        const msUntilNext = getNextDeliveryIn();
        const delay = msUntilNext === null ? 30000 : Math.min(5000, msUntilNext + 500);
        pollIntervalRef.current = setTimeout(() => {
          fetchUnreadCount();
          fetchNotifications();
          schedulePoll();
        }, delay);
      };
      schedulePoll();
    } else {
      pollIntervalRef.current = setInterval(() => {
        fetchUnreadCount();
        fetchNotifications();
      }, 60000);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearTimeout(pollIntervalRef.current);
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isAuthenticated, fetchUnreadCount, fetchNotifications, TOASTED_STORAGE_KEY]);

  useEffect(() => {
    checkForNewToasts(notifications);
  }, [notifications, checkForNewToasts]);

  const value: NotificationContextValue = {
    notifications,
    unreadCount,
    total,
    loading,
    error,
    isPanelOpen,
    filter,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    togglePanel,
    openPanel,
    closePanel,
    changeFilter
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
